/**
 * AwarenessFrontend — Client-Side Awareness with rAF Throttling
 *
 * sl-lv2w.9: Reduces WebSocket message rate for awareness updates by
 * batching outgoing updates to max 1x per animation frame (16ms / 60fps).
 *
 * Architecture:
 * - Outgoing: Local awareness changes accumulate changed client IDs in a Set.
 *   A single rAF callback encodes ALL accumulated changes into one update.
 * - Incoming: Remote awareness updates are applied via rAF for smooth rendering.
 * - Local cursor: Zero lag — Y.js applies local state immediately in-memory.
 *   Only the network send is throttled.
 *
 * SOLID:
 * - S: Single Responsibility — Only handles awareness frontend ↔ sync bridge
 * - D: Dependency Inversion — Depends on AwarenessSync abstraction
 */
import { nanoid } from 'nanoid';
import {
  applyAwarenessUpdate,
  type Awareness,
  encodeAwarenessUpdate,
} from 'y-protocols/awareness';

import type { AwarenessRecord } from '../storage/awareness';
import type { AwarenessSync } from '../sync/awareness';

type AwarenessChanges = Record<'added' | 'updated' | 'removed', number[]>;

/**
 * Schedule a callback via requestAnimationFrame (browser) or setTimeout 0 (Node/test).
 * Returns a cancel function.
 */
function scheduleFrame(callback: () => void): () => void {
  if (typeof requestAnimationFrame === 'function') {
    const id = requestAnimationFrame(callback);
    return () => cancelAnimationFrame(id);
  }
  // Fallback for Node.js / test environments
  const id = setTimeout(callback, 0);
  return () => clearTimeout(id);
}

export class AwarenessFrontend {
  constructor(private readonly sync: AwarenessSync) {}

  connectAwareness(awareness: Awareness) {
    const uniqueId = nanoid();

    // --- Outgoing throttle (sl-lv2w.9) ---
    // Accumulate changed client IDs between frames. A single rAF callback
    // encodes all accumulated changes into one awareness update message.
    const pendingOutgoing = new Set<number>();
    let cancelOutgoingFrame: (() => void) | null = null;

    const flushOutgoing = () => {
      cancelOutgoingFrame = null;
      if (pendingOutgoing.size === 0) {
        return;
      }
      const clients = Array.from(pendingOutgoing);
      pendingOutgoing.clear();

      const update = encodeAwarenessUpdate(awareness, clients);
      this.sync
        .update(
          {
            docId: awareness.doc.guid,
            bin: update,
          },
          uniqueId
        )
        .catch(error => {
          console.error('update awareness error', error);
        });
    };

    const handleAwarenessUpdate = (
      changes: AwarenessChanges,
      origin: string
    ) => {
      if (origin === uniqueId) {
        return;
      }

      // Collect changed clients — multiple changes within one frame are merged
      for (const clientIds of Object.values(changes)) {
        for (const id of clientIds) {
          pendingOutgoing.add(id);
        }
      }

      // Schedule flush if not already scheduled
      if (!cancelOutgoingFrame) {
        cancelOutgoingFrame = scheduleFrame(flushOutgoing);
      }
    };

    awareness.on('change', handleAwarenessUpdate);

    // --- Incoming throttle (sl-lv2w.9) ---
    // Buffer incoming remote updates and apply them in the next animation frame
    // for smooth remote cursor rendering.
    let pendingIncoming: AwarenessRecord[] = [];
    let cancelIncomingFrame: (() => void) | null = null;

    const flushIncoming = () => {
      cancelIncomingFrame = null;
      const updates = pendingIncoming;
      pendingIncoming = [];

      for (const update of updates) {
        applyAwarenessUpdate(awareness, update.bin, uniqueId);
      }
    };

    const handleSyncUpdate = (update: AwarenessRecord, origin?: string) => {
      if (origin === uniqueId) {
        // skip self update
        return;
      }

      pendingIncoming.push(update);

      if (!cancelIncomingFrame) {
        cancelIncomingFrame = scheduleFrame(flushIncoming);
      }
    };

    const handleSyncCollect = () => {
      return Promise.resolve({
        docId: awareness.doc.guid,
        bin: encodeAwarenessUpdate(awareness, [awareness.clientID]),
      });
    };
    const unsubscribe = this.sync.subscribeUpdate(
      awareness.doc.guid,
      handleSyncUpdate,
      handleSyncCollect
    );

    const cleanup = () => {
      awareness.off('change', handleAwarenessUpdate);
      // Flush any pending outgoing updates immediately on disconnect
      if (cancelOutgoingFrame) {
        cancelOutgoingFrame();
        cancelOutgoingFrame = null;
      }
      flushOutgoing();
      // Cancel pending incoming frame (no point applying after disconnect)
      if (cancelIncomingFrame) {
        cancelIncomingFrame();
        cancelIncomingFrame = null;
      }
      pendingIncoming = [];
      unsubscribe();
    };

    awareness.once('destroy', cleanup);

    return cleanup;
  }
}
